import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Layout from "./components/Layout";
import { callGetWebservice } from "./hooks/api";
import useFirebaseMessaging from "./hooks/useFirebaseMessaging";
import useFirebaseTokenUpdate from "./hooks/useFirebaseTokenUpdate";
import AboutUs from "./pages/AboutUs";
import AddAccountAddress from "./pages/AddAccountAddress";
import AddCard from "./pages/AddCard";
import BookEvent from "./pages/BookEvent";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CompleteOrder from "./pages/CompleteOrder";
import ContactUs from "./pages/ContactUs";
import Faqs from "./pages/Faqs";
import Forgot from "./pages/Forgot";
import Home from "./pages/Home";
import Login from "./pages/Login";
import MyAccount from "./pages/MyAccount";
import MyAddresses from "./pages/MyAddress";
import MyOrders from "./pages/MyOrders";
import NotFound from "./pages/NotFound";
import OrderDetail from "./pages/OrderDetail";
import PageNotFound from "./pages/PageNotFound";
import AcceptedOrder from "./pages/pay/AcceptedOrder";
import AcceptedOrderThankYouPage from "./pages/pay/AcceptedOrderThankYouPage";
import OrderPayment from "./pages/pay/OrderPayment";
import PaymentReceipt from "./pages/pay/PaymentReceipt";
import PaymentMethod from "./pages/PaymentMethod";
import Privacy from "./pages/Privacy";
import ProductDetail1 from "./pages/ProductDetail1";
import ProductList from "./pages/ProductList";
import ReturnExchange from "./pages/ReturnExchange";
import ShippingPolicy from "./pages/ShippingPolicy";
import SocialAuthCallBack from "./pages/SocialAuthCallBack";
import Stores from "./pages/Stores";
import TermsConditions from "./pages/TermsConditions";
import TrackYourOrder from "./pages/TrackYourOrder";
import Wish from "./pages/Wish";
import { setUser } from "./store/reducers/auth";
import { setMerchantSettings, setTheme } from "./store/reducers/general";
import { appTheme, appToken, userData } from "./utils/constants";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "./utils/localStorageUtils";
import { printLogs, shouldShowTokenPopup } from "./utils/Utilities";
import MallRestaurants from "./pages/MallRestaurants";

function App() {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const theme = useSelector((state) => state.general.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    currentToken,
    messageReceived,
    error: firebaseError,
    requestPermission,
  } = useFirebaseMessaging();
  const { updateFirebaseTokenInStorage, updateFirebaseTokenAPI } =
    useFirebaseTokenUpdate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showTokenPopup, setShowTokenPopup] = useState(true);
  const merchantSettings = useSelector(
    (state) => state.general.merchantSettings,
  );

  const fetchMerchantSettings = async () => {
    if (!merchantSettings || Object.keys(merchantSettings).length === 0) {
      const requestPacket = {};
      try {
        callGetWebservice(
          "merchant",
          requestPacket,
          (response) => {
            dispatch(setMerchantSettings(response.data));
            setLoading(false);
          },
          (error) => {
            setLoading(false);
            setError(error.errMessage);
          },
        );
      } catch (error) {
        console.error("Error fetching merchant settings:", error);
      }
    } else {
      setLoading(false);
    }
  };

  const initializeApp = async () => {
    try {
      // clearLocalStorageExceptUser();

      let data = getFromLocalStorage(userData);

      if (data) {
        dispatch(setUser(data));
      }

      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const domain = `${protocol}//${hostname}${port ? ":" + port : ""}`;

      let apiUrl =
        process.env.REACT_APP_API_URL + `get-merchant-theme?domain=${domain}`;
      const urlResponse = await axios.get(apiUrl);
      const { cToken, cTheme } = urlResponse.data;

      if (!urlResponse.data) {
        printLogs("Data not found.");
        setError("The website is not working. Please try again later.");
        return;
      }

      saveToLocalStorage(appToken, cToken);
      saveToLocalStorage(appTheme, cTheme);
      dispatch(setTheme(cTheme));

      await fetchMerchantSettings();
    } catch (error) {
      console.log("error -  initializing app:", error);
      setLoading(false);
      setError("The website is not working. Please try again later.");
    }
  };

  useEffect(() => {
    const path = window.location.pathname;

    if (
      !path.includes("order-payment") &&
      !path.includes("accepted-order") &&
      !path.includes("order-accepted-success") &&
      !path.includes("/payment") &&
      !path.includes("/malls/")
    ) {
      initializeApp();
    } else {
      setLoading(false);
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderNo = params.get("order_no");
    const amount = params.get("amount");
    const currency = params.get("currency");
    const merchantId = params.get("merchantId");

    if (orderNo) {
      let basePath = "/order-payment";
      if (location.pathname.includes("/accepted-order")) {
        basePath = "/accepted-order";
      }

      let url = `${basePath}/${orderNo}`;
      const queryParams = [];
      if (amount) queryParams.push(`amount=${amount}`);
      if (currency) queryParams.push(`currency=${currency}`);
      if (merchantId) queryParams.push(`merchantId=${merchantId}`);
      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }
      navigate(url, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (currentToken) {
      updateFirebaseTokenInStorage(currentToken);
    }
  }, [currentToken]);

  useEffect(() => {
    if (merchantSettings?.user?.name) {
      document.title = merchantSettings?.user.name;
    }
  }, [merchantSettings]);

  if (loading) {
    return (
      <div className="premium-loader-container">
        <div className="premium-loader-content">
          <div className="premium-spinner-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <h3 className="premium-loader-title">Almost Ready</h3>
          <p className="premium-loader-text">
            Fetching the latest content and preparing your experience...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="premium-error-container">
        <div className="premium-error-card">
          <div className="premium-error-icon-wrapper">
            <i className="fas fa-shopping-bag premium-bag-icon"></i>
            <div className="premium-error-badge">
              <i className="fas fa-exclamation"></i>
            </div>
          </div>
          <h3 className="premium-error-title">Oops! Something went wrong</h3>
          <p className="premium-error-text">{error}</p>
          <button
            className="btn btn-premium premium-error-btn"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-redo-alt mr-2"></i> Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {shouldShowTokenPopup() &&
        (currentToken || firebaseError) &&
        showTokenPopup && (
          <div
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 9999,
              backgroundColor: "#fff",
              padding: "15px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid #e0e0e0",
              maxWidth: "320px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Firebase Token
              </h4>
              <button
                onClick={() => setShowTokenPopup(false)}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  color: "#999",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>
            {firebaseError ? (
              <>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#d9534f",
                    backgroundColor: "#fdf1f0",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                >
                  Error: {firebaseError}
                </p>
                {(firebaseError === "Notification permission is required." ||
                  firebaseError === "Notification permission not granted.") && (
                  <button
                    onClick={requestPermission}
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      padding: "6px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Enable Notifications
                  </button>
                )}
              </>
            ) : (
              <>
                <p
                  style={{
                    margin: 0,
                    wordBreak: "break-all",
                    fontSize: "12px",
                    color: "#666",
                    maxHeight: "100px",
                    overflowY: "auto",
                    backgroundColor: "#f5f5f5",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                >
                  {currentToken}
                </p>
                <button
                  onClick={() => navigator.clipboard.writeText(currentToken)}
                  style={{
                    marginTop: "10px",
                    width: "100%",
                    padding: "6px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Copy Token
                </button>
              </>
            )}
          </div>
        )}
      <Routes>
        <Route path="/order-payment/:slug?" element={<OrderPayment />} />
        <Route path="/accepted-order/:slug?" element={<AcceptedOrder />} />
        <Route
          path="/order-accepted-success"
          element={<AcceptedOrderThankYouPage />}
        />
        <Route path="/payment-receipt" element={<PaymentReceipt />} />
        <Route path="/malls/:token" element={<MallRestaurants />} />
        <Route
          path="*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/aboutus" element={<AboutUs />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/shipping" element={<ShippingPolicy />} />
                <Route path="/return" element={<ReturnExchange />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot" element={<Forgot />} />
                <Route path="/productdetail" element={<ProductDetail1 />} />
                <Route path="/faqs" element={<Faqs />} />
                <Route path="/completeorder" element={<CompleteOrder />} />
                <Route path="/track" element={<TrackYourOrder />} />
                <Route path="/productlist" element={<ProductList />} />
                <Route
                  path="/social-auth/:provider/callback"
                  element={<SocialAuthCallBack />}
                />

                {isLoggedIn ? (
                  <>
                    <Route path="/wish" element={<Wish />} />
                    <Route path="/myaccount" element={<MyAccount />} />
                    <Route path="/myorders" element={<MyOrders />} />
                    <Route
                      path="/orderdetail/:slug"
                      element={<OrderDetail />}
                    />
                    <Route path="/myaddress" element={<MyAddresses />} />
                    <Route path="/addaddress" element={<AddAccountAddress />} />
                  </>
                ) : (
                  // Redirect unauthenticated users
                  <Route path="*" element={<PageNotFound />} />
                )}

                <Route path="/paymentmethod" element={<PaymentMethod />} />
                <Route path="/contactus" element={<ContactUs />} />
                <Route path="/addcard" element={<AddCard />} />
                <Route path="/store" element={<Stores />} />
                <Route path="/book-event" element={<BookEvent />} />
                <Route path="/not" element={<NotFound />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </Layout>
          }
        />
      </Routes>
    </>
  );
}

export default App;
